import {
  AdasGrid,
  AdasButton,
  AdasFormControl,
  AdasTypography,
  AdasInputLabel,
  AdasSelect,
  AdasButtonGroup,
  AdasBox,
  AdasCard
} from '@components/wrapper-components'
import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  getTimeZones,
  getOrganisationByReferenceId,
  updateEnableSchedulerForCalibrationCenter,
  updateTimeZoneOffsetForOrganisation,
  updateReferralSourceToggle
} from 'api/api'
import { Logger } from '../../../logger'
import { useLoadingStore, useOrganisationStore } from '@caradasstore'

export const SchedulerSettings = ({ isAdminUser, ccId, calibrationCenterData, refetchCalibrationCenter }) => {
  const setOrganisationDetail = useOrganisationStore((store) => store.setOrganisationDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [calenderToggleEnabled, setCalenderToggleEnabled] = useState(null)
  const [toggleStates, setToggleStates] = useState({
    PGW: null,
    SF: null,
    ENTERPRISE: null,
    GEICO: null,
    AS: null,
    BRI: null
  })
  const [timeZones, setTimeZones] = useState([])
  const [isOrganisationConfigured, setIsOrganisationConfigured] = useState(false)
  const [selectedTimeZone, setSelectedTimeZone] = useState('')

  useEffect(() => {
    if (calibrationCenterData) {
      setCalenderToggleEnabled(!!calibrationCenterData.is_scheduler_enabled)
      setToggleStates({
        PGW: !!calibrationCenterData.is_pgw_enabled,
        SF: !!calibrationCenterData.is_sf_enabled,
        ENTERPRISE: !!calibrationCenterData.is_enterprise_enabled,
        GEICO: !!calibrationCenterData.is_geico_enabled,
        AS: !!calibrationCenterData.is_allstate_enabled,
        BRI: !!calibrationCenterData.is_bearriver_enabled
      })
    }
  }, [calibrationCenterData])

  const fetchOrganisationDetailsByCcId = useCallback(async () => {
    setLoading(true)
    try {
      const { data, status } = await getOrganisationByReferenceId({ ccId })
      if (status === 200 && data?.id) {
        setSelectedTimeZone(data.timezone_at)
        setOrganisationDetail(data)
        setIsOrganisationConfigured(true)
      } else {
        setIsOrganisationConfigured(false)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'schedulerSettings', method: 'getOrganisationByReferenceId' } })
    } finally {
      setLoading(false)
    }
  }, [ccId])

  const fetchTimeZones = useCallback(async () => {
    try {
      const { data } = await getTimeZones({ ccId })
      if (data) {
        const zones = data.map(({ timezone_offset, name, timezone_offset_value }) => ({
          id: timezone_offset,
          value: name,
          timezone_offset_value
        }))
        setTimeZones(zones)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'schedulerSetting', ccId } })
    }
  }, [ccId])

  useEffect(() => {
    if (ccId > 0) {
      fetchTimeZones()
      fetchOrganisationDetailsByCcId()
    }
  }, [ccId, fetchOrganisationDetailsByCcId, fetchTimeZones])

  const handleReferralToggle = async (networkKey, isEnabled) => {
    setLoading(true)
    try {
      await updateReferralSourceToggle(ccId, isEnabled, networkKey)
      refetchCalibrationCenter()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'schedulerSettings', method: 'handleReferralToggle', ccId, isEnabled, networkKey } })
    } finally {
      setLoading(false)
    }
  }

  const handleCalendarToggle = async (e) => {
    const isEnabled = e.target.value === 'Yes'
    setCalenderToggleEnabled(isEnabled)
    setLoading(true)
    try {
      await updateEnableSchedulerForCalibrationCenter(ccId, isEnabled)
      refetchCalibrationCenter()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'schedulerSettings', method: 'updateSchedulerEnabled', ccId, isEnabled } })
    } finally {
      setLoading(false)
    }
  }

  const handleReferralNetworkToggle = (networkKey) => async (e) => {
    const isEnabled = e.target.value === 'Yes'
    setToggleStates((prev) => ({ ...prev, [networkKey]: isEnabled }))
    await handleReferralToggle(networkKey, isEnabled)
  }

  const timeZoneChangeHandler = async (event) => {
    setLoading(true)
    try {
      await updateTimeZoneOffsetForOrganisation(ccId, event.target.value)
      fetchOrganisationDetailsByCcId()
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'schedulerSettings', ccId, timeZoneOffset: event.target.value } })
    } finally {
      setLoading(false)
    }
  }

  const referralNetworks = useMemo(
    () => [
      {
        key: 'PGW',
        name: 'Car ADAS PGW Glass Referral Network',
        description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from PGW'
      },
      {
        key: 'SF',
        name: 'State Farm',
        description:
          'Calibration Center will participate in referral network and receive calibration appointments scheduled from State Farm.'
      },
      {
        key: 'ENTERPRISE',
        name: 'Enterprise Rent-A-Car',
        description:
          'Calibration Center will participate in referral network and receive calibration appointments scheduled from Enterprise Rent-A-Car.'
      },
      {
        key: 'GEICO',
        name: 'GEICO',
        description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from GEICO.'
      },
      {
        key: 'AS',
        name: 'Allstate',
        description: 'Calibration Center will participate in referral network and receive calibration appointments scheduled from Allstate.'
      },
      {
        key: 'BRI',
        name: 'Bear River Insurance',
        description:
          'Calibration Center will participate in referral network and receive calibration appointments scheduled from Bear River Insurance.'
      }
    ],
    []
  )

  return (
    <>
      <AdasTypography component='span' fontWeight='bold' sx={{ marginTop: '20px' }} variant='h6'>
        Scheduler Settings (Admin Console)
      </AdasTypography>
      <AdasCard sx={{ margin: '17px 0px', padding: '16px' }}>
        <AdasBox>
          {/* Calendar Section */}
          <AdasTypography sx={{ fontWeight: '600', marginBottom: '5px' }}>Calendar</AdasTypography>
          {isAdminUser && (
            <AdasGrid container alignItems='flex-start' sx={{ mt: '10px' }}>
              <AdasGrid item xs={12} sm={8} md={9}>
                <AdasTypography variant='body2'>
                  Click &apos;Yes&apos; to activate Scheduler and Calendar in Car ADAS Plus for the calibration center. Once enabled,
                  Scheduler will display in left navigation bar and external scheduling link will be active..
                </AdasTypography>
              </AdasGrid>
              <AdasGrid item xs={12} sm={4} md={3} container justifyContent='flex-end'>
                <AdasButtonGroup
                  type='toggleButton'
                  value={calenderToggleEnabled ? 'Yes' : 'No'}
                  exclusive
                  onChange={handleCalendarToggle}
                  aria-label='opt-in'
                  name='optIn'
                  sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: '10px', sm: 0 } }}
                >
                  {['Yes', 'No'].map((val) => (
                    <AdasButton
                      key={val}
                      buttonType='toggleButton'
                      value={val}
                      color='primary'
                      aria-label={val}
                      sx={{ padding: '5px 10px', flex: { xs: 1, sm: 'none' } }}
                    >
                      {val}
                    </AdasButton>
                  ))}
                </AdasButtonGroup>
              </AdasGrid>
            </AdasGrid>
          )}

          {calenderToggleEnabled &&
            referralNetworks.map(({ key, name, description }) => (
              <div key={key}>
                <AdasTypography sx={{ fontWeight: '600', mt: '10px', mb: '5px' }}>{name}</AdasTypography>
                <AdasGrid container alignItems='flex-start' sx={{ mt: '10px' }}>
                  <AdasGrid item xs={12} sm={8} md={9}>
                    <AdasTypography variant='body2'>{description}</AdasTypography>
                  </AdasGrid>
                  <AdasGrid item xs={12} sm={4} md={3} container justifyContent='flex-end'>
                    <AdasButtonGroup
                      type='toggleButton'
                      value={toggleStates[key] ? 'Yes' : 'No'}
                      exclusive
                      onChange={handleReferralNetworkToggle(key)}
                      aria-label='opt-in'
                      name='optIn'
                      sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: '10px', sm: 0 } }}
                    >
                      {['Yes', 'No'].map((val) => (
                        <AdasButton
                          key={val}
                          buttonType='toggleButton'
                          value={val}
                          color='primary'
                          aria-label={val}
                          sx={{ padding: '5px 10px', flex: { xs: 1, sm: 'none' } }}
                        >
                          {val}
                        </AdasButton>
                      ))}
                    </AdasButtonGroup>
                  </AdasGrid>
                </AdasGrid>
              </div>
            ))}

          {isAdminUser && isOrganisationConfigured && calenderToggleEnabled && (
            <>
              <AdasTypography sx={{ fontWeight: '600', mt: '10px' }}>Time Zone</AdasTypography>
              <AdasGrid container spacing={2} alignItems='center' sx={{ mb: '10px' }}>
                <AdasGrid container alignItems='center' sx={{ mt: '10px', ml: '16px' }}>
                  <AdasGrid item xs={12} sm={8}>
                    <AdasTypography variant='body2'>Select the calibration center time zone</AdasTypography>
                  </AdasGrid>
                  <AdasGrid
                    item
                    xs={12}
                    sm={4}
                    container
                    justifyContent='flex-end'
                    alignItems='center'
                    sx={{ mt: { xs: '15px', md: '0px' } }}
                  >
                    <AdasFormControl fullWidth>
                      <AdasInputLabel id='timeZoneLabelId'>Time Zone</AdasInputLabel>
                      <AdasSelect
                        labelId='timeZoneLabelId'
                        id='timeZone'
                        value={selectedTimeZone}
                        label='Time Zone'
                        onChange={timeZoneChangeHandler}
                        options={timeZones}
                      />
                    </AdasFormControl>
                  </AdasGrid>
                </AdasGrid>
              </AdasGrid>
            </>
          )}
        </AdasBox>
      </AdasCard>
    </>
  )
}
